"use client";

import { ArrowUpRight } from "lucide-react";
import {
  ApplicableCollectionsKeyType,
  ApplicableProductsKeyType,
  ApplicableProductsTypeType,
  PageType,
} from "@/types/coupon/discount";
import React, { useEffect, useRef, useState } from "react";
import { ButtonPrimary } from "../common/ButtonPrimary";
import { StandardButton } from "../common/StandardButton";
import { InputPrimary } from "../common/InputPrimary";
import { Dialog, DialogContent, DialogHeader } from "../ui/dialog";
import ProductsSelection from "./productsSelection";
import CollectionsSelection from "./CollectionsSelection";

const SearchProducts = ({
  currentPage,
  applicableProductsType,
  applicableProductsKey,
  applicableCollectionsKey,
  initialTab = "all",
  openInSelectedTabRef,
}: {
  currentPage: PageType;
  applicableProductsType: ApplicableProductsTypeType;
  applicableProductsKey: ApplicableProductsKeyType;
  applicableCollectionsKey: ApplicableCollectionsKeyType;
  initialTab?: "all" | "selected";
  openInSelectedTabRef?: React.MutableRefObject<(() => void) | undefined>;
}) => {
  const [inputValue, setInputValue] = useState("");
  const [open, setOpen] = useState(false);
  const [collectionsTab, setCollectionsTab] = useState<"all" | "selected">(
    initialTab,
  );
  const [activeTab, setActiveTab] = useState<"all" | "selected">(initialTab);
  // ...existing code...
  const typingTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const saveTriggerRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
    }
  }, [open]);

  useEffect(() => {
    if (open || inputValue === "") return;

    if (typingTimeout.current) clearTimeout(typingTimeout.current);

    typingTimeout.current = setTimeout(() => {
      setOpen(true);
    }, 1000);

    return () => {
      if (typingTimeout.current) clearTimeout(typingTimeout.current);
    };
  }, [inputValue, open]);

  const handleOuterInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  // ...existing code...

  const handleModalInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      setInputValue("");
      if (typingTimeout.current) {
        clearTimeout(typingTimeout.current);
      }
    }
  };

  // Expose a function to open in selected tab
  React.useEffect(() => {
    if (openInSelectedTabRef) {
      openInSelectedTabRef.current = () => {
        setCollectionsTab("selected");
        setOpen(true);
      };
    }
  }, [openInSelectedTabRef]);

  const closeModal = () => {
    setOpen(false);
    setInputValue("");
    setCollectionsTab(initialTab);
    setActiveTab(initialTab);
  };

  const handleTabChange = (tab: "all" | "selected") => {
    setActiveTab(tab);
  };

  return (
    <>
      <div className="flex gap-[6px] h-[50px]">
        <InputPrimary
          value={inputValue}
          onChange={handleOuterInputChange}
          className="rectangle-full h-full !bg-background"
          placeholder="Search"
        />
        <ButtonPrimary
          className="rectangle-full h-full !px-8"
          onClick={() => setOpen(true)}
        >
          Browse Item
          <ArrowUpRight className="!w-[26px] !h-[26px]" />
        </ButtonPrimary>
      </div>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="w-full min-w-[600px] max-w-4xl rounded-xl bg-secondary-background [&>button:last-child]:hidden p-6 pt-4 flex flex-col">
          <DialogHeader>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-foreground">
                Select Products
              </h2>
              <div className="flex items-center gap-3">
                <StandardButton
                  variant="secondary"
                  onClick={() => {
                    setOpen(false);
                  }}
                >
                  Close
                </StandardButton>
                <ButtonPrimary
                  onClick={() => {
                    // Trigger save functionality from child component
                    if (saveTriggerRef.current) {
                      saveTriggerRef.current();
                    }
                    setOpen(false);
                  }}
                >
                  Save
                </ButtonPrimary>
              </div>
            </div>
          </DialogHeader>
          <div
            className={`flex flex-col overflow-hidden mt-4 ${activeTab !== "selected" ? "space-y-6" : ""}`}
          >
            {activeTab !== "selected" && (
              <InputPrimary
                ref={inputRef}
                value={inputValue}
                onChange={handleModalInputChange}
                className="rounded-full h-[50px] !bg-background flex-shrink-0 px-4 border border-border"
                placeholder="Search"
              />
            )}
            <div className="space-y-[3px] overflow-y-auto">
              {applicableProductsType === "specific" ? (
                <ProductsSelection
                  onClose={closeModal}
                  currentPage={currentPage}
                  applicableProductsKey={applicableProductsKey}
                  searchValue={inputValue}
                  activeTab={activeTab}
                  onTabChange={handleTabChange}
                  saveTriggerRef={saveTriggerRef}
                />
              ) : (
                <CollectionsSelection
                  currentPage={currentPage}
                  applicableCollectionsKey={applicableCollectionsKey}
                  searchValue={inputValue}
                  onClose={closeModal}
                  initialTab={collectionsTab}
                  activeTab={activeTab}
                  onTabChange={handleTabChange}
                  saveTriggerRef={saveTriggerRef}
                />
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SearchProducts;
